<template>
    <div ref="container" class="vs-chart-container" :style="reactiveContainerStyle">
        <slot></slot>
        <svg v-if="draw" class="vs-chart-svg" :style="reactiveSvgStyle">
            <!-- common -->
            <g class="x-axis" :transform="`translate(-${boundary.minX}, 0)`" v-if="rects.length > 0">
                <AxisXLine
                    v-if="xAxis"
                    :boundary="boundary"
                    :strokeColor="strokeColor"
                >
                </AxisXLine>
                <AxisXLabel
                    v-if="xLabel"
                    :labelRotate="labelRotate"
                    :boundary="boundary"
                    :labelColor="labelColor"
                    :tickSize="tickSize"
                    :tickPadding="tickPadding"
                    :points="xLabelPoints"
                    :strokeColor="strokeColor"
                >
                    <template v-slot:x-axis="{ item }">
                        <slot :item="{ ...item }" name="x-axis"></slot>
                    </template>
                </AxisXLabel>
            </g>
            <g class="y-axis" :transform="`translate(-${boundary.minX}, 0)`">
                <AxisYLine
                    v-if="yAxis"
                    :boundary="boundary"
                    :tickPadding="tickPadding"
                ></AxisYLine>
                <AxisYLabel
                    v-if="yLabel"
                    :boundary="boundary"
                    :labelColor="labelColor"
                    :tickSize="tickSize"
                    :tickPadding="tickPadding"
                    :points="yPoints"
                >
                    <template v-slot:y-axis="{ item }">
                        <slot :item="{ ...item }" name="y-axis"></slot>
                    </template>
                </AxisYLabel>
            </g>
            <Legend v-if="rects.length > 0"
                :yKeys="yKeys"
                :legendSize="legendSize"
                :legendPadding="legendPadding"
                :legendDirection="legendDirection"
                :boundary="boundary"
                :colors="barColors"
                :labelColor="labelColor"
            ></Legend>
            <g class="grid">
                <g class="y-grid" v-if="yGrid !== 0">
                    <line v-for="(point, index) in yPoints.slice(0, yPoints.length -1)" :key="index"
                        :x1="boundary.minX"
                        :x2="boundary.maxX - boundary.padding"
                        :y1="boundary.minY + point.y"
                        :y2="boundary.minY + point.y"
                        :stroke="strokeColor"
                    ></line>
                </g>
                <g class="x-grid" v-if="xGrid !== 0 && rects.length > 0">
                    <line v-for="(point, index) in xPoints.filter((point, index) => index !== 0 && index % xGrid === 0)" :key="index"
                        :x1="point.x"
                        :x2="point.x"
                        :y1="boundary.padding"
                        :y2="boundary.maxY"
                        :stroke="strokeColor"
                    >
                    </line>
                </g>
            </g>
            <g class="bar-label" v-if="barLabel">
                <template v-if="stacked">
                    <text v-for="(point, index) in stackedRect" :key="index"
                        style="text-anchor:middle;"
                        :fill="labelColor"
                        :font-size="`${tickSize}rem`"
                        :x="point.x + (point.width / 2)"
                        :y="point.value < 0 ? boundary.maxY - startY - barLabelPadding : (boundary.maxY - point.height - barLabelPadding - startY)"
                        :title="`${point.key}: ${point.title}`">
                        <slot name="bar-label" :item="{ ...point, index }"></slot>
                    </text>
                </template>
                <template v-else>
                    <text v-for="(point, index) in rects" :key="index"
                        style="text-anchor:middle;"
                        :fill="labelColor"
                        :font-size="`${tickSize}rem`"
                        :x="point.x + (point.width / 2)"
                        :y="point.value < 0 ? boundary.maxY - startY - barLabelPadding : (boundary.maxY - point.height - barLabelPadding - startY)"
                        :title="point.title">
                        <slot name="bar-label" :item="{ ...point, index }"></slot>
                    </text>
                </template>
            </g>
            <g class="bar"
                :transform="`scale(1, -1)  translate(0, ${-boundary.maxY})`"
            >
                <defs>
                    <filter id="bar-shadow" x="0" y="0" width="200%" height="200%">
                        <feOffset result="offOut" in="SourceAlpha" dx="2" dy="-5"/>
                        <feGaussianBlur result="blurOut" in="offOut" stdDeviation="2" />
                        <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
                    </filter>
                </defs>
                <template v-if="stacked && barShadowFilter">
                    <rect v-for="(point, index) in stackedRect" :key="`stacked-${index}`"
                        fill="#fff"
                        filter="url(#bar-shadow)"
                        :x="point.x"
                        :y="0"
                        :width="point.width < 0 ? 0 : point.width"
                        :height="point.height"
                        :rx="stacked ? 0 : rounding"
                        :ry="stacked ? 0 : rounding"
                    >
                    </rect>
                </template>
                <rect v-for="(point, index) in rects" :key="index"
                    :style="`opacity:${isActive(index) ? 1 : opacity || 1};cursor:pointer;`"
                    :id="`bar-id-${point.key}`"
                    :fill="point.value === 0 ? noneColor : (isActive(index) ? active.color : barColors[point.key])"
                    :filter="barShadowFilter && !stacked ? 'url(#bar-shadow)' : 'none'"
                    :x="point.x"
                    :y="point.value < 0 ? point.y + startY - point.height : point.y + startY"
                    :width="point.width < 0 ? 0 : point.width"
                    :height="point.height"
                    :rx="stacked ? 0 : rounding"
                    :ry="stacked ? 0 : rounding"
                    @click="handlerBarClick($event, point)"
                    @mouseover="handlerMouseOver($event, point, index)"
                    @mouseout="handlerMouseOut($event, point)"
                >
                        <animate v-if="!stacked && animation"
                            attributeName="height"
                            :from="0"
                            :to="point.height"
                            :dur="`${growDurations}s`"
                            fill="freeze"
                        >
                        </animate>
                </rect>
            </g>
            <slot :item="{ ...hoverData }" name="tooltip"></slot>
        </svg>
    </div>
</template>

<script>
import { genRect } from '../helper/rect'
import { genYaxisPoints, genBarXaxisPoints } from '../helper/axis'
import commonProps from '../mixins/commonProps'
import barProps from '../mixins/barProps'
import Legend from './Legend'
import AxisXLine from './AxisXLine'
import AxisXLabel from './AxisXLabel'
import AxisYLine from './AxisYLine'
import AxisYLabel from './AxisYLabel'
export default {
    name: 'vs-svg-bar',
    components: {
        AxisXLine,
        AxisXLabel,
        AxisYLine,
        AxisYLabel,
        Legend
    },
    mixins: [
        commonProps,
        barProps
    ],
    data () {
        return {
            draw: true,
            hoverIndex: -1,
            hoverData: {},
            offsetX: 0,
            rects: [],
            stackedRect: [],
            widthValue: 0
        }
    },
    computed: {
        startY () {
            if (this.min < 0) {
                const zeroPoint = this.yPoints.filter(point => point.label === 0)
                return zeroPoint.length > 0 ? this.boundary.maxY - this.padding - zeroPoint[0].y : 0
            } else {
                return 0
            }
        },
        xLabelPoints () {
            return this.xPoints.map(item => {
                return ({ x: (item.x + (this.offsetX / 2)), label: item.label })
            })
        },
        xPoints () {
            return genBarXaxisPoints(this.data, this.xKey, this.boundary)
        },
        yPoints () {
            return genYaxisPoints({ yGrid: this.yGrid }, this.boundary, { min: this.min, max: this.max }, true)
        },
        reactiveContainerStyle () {
            return {
                'padding-top': this.height && this.width && this.draw ? `${this.height / this.width * (this.width / this.widthValue * 100)}%` : undefined
            }
        },
        reactiveSvgStyle () {
            const width = this.width / this.widthValue * 100
            return {
                height: `${this.height}px`,
                width: `${width}%`
            }
        },
        max () {
            if (this.maximumMax) {
                return this.maximumMax
            } else {
                const max = Math.max(...this.values, null)
                return max < 1 ? this.minimumMax : max
            }
        },
        min () {
            return Math.min(...this.values, null)
        },
        grid () {
            const defaultYGrid = this.data.length / 2 > 6 ? 6 : (this.data.length / 2 < 1 ? 4 : this.data.length)
            return {
                xGrid: this.xGrid || this.data.length,
                yGrid: this.yGrid || defaultYGrid
            }
        },
        // bar
        values () {
            const { data, yKeys, stacked } = this
            if (stacked) {
                const reducer = (accumulator, currentValue) => accumulator + currentValue
                return data.map(item => yKeys.map(key => item[key]).reduce(reducer, 0))
            } else {
                const inArr = []
                data.forEach(item => yKeys.forEach(key => inArr.push(item[key])))
                return inArr
            }
        },
        renderOptions () {
            const { yKeys } = this
            return {
                yKeys
            }
        },
        boundary () {
            const { padding, width, height } = this
            const maxValue = Math.max.apply(null, this.values)
            const maxLength = maxValue.toFixed(this.maxDecimal).length
            const isZero = this.values.some(value => value < 1)
            let margin = 0
            if (this.marginLeft === 'auto') {
                margin = maxLength * 5
                if (isZero && margin < 30) {
                    margin = 30
                }
            } else {
                margin = this.marginLeft
            }
            return {
                minX: padding + margin,
                minY: padding,
                maxX: width - padding,
                maxY: height - padding - padding,
                padding,
                marginLeft: margin
            }
        }
    },
    mounted () {
        this.$nextTick(() => {
            if (this.width && this.height) {
                this.makeDrawData()
            }
            window.addEventListener('resize', this.makeDrawData)
        })
    },
    beforeUpdate () {
        this.$el.querySelectorAll('animate').forEach(el => el.remove())
    },
    beforeDestroy () {
        window.removeEventListener('resize', this.makeDrawData)
    },
    methods: {
        isActive (index) {
            return this.active && this.active.condition(index)
        },
        handlerBarClick (event, data) {
            if (this.$listeners['click:point']) {
                this.handlerMouseOut(null)
                this.$emit('click:point', { event, data })
            }
        },
        handlerMouseOver (event, data, index) {
            if (!this.hover && this.$listeners['mouseover:point']) {
                this.hoverIndex = this.stacked ? parseInt(index / this.yKeys.length) : index
                this.hover = true
                this.hoverData = {
                    label: this.stacked ? this.xPoints[this.hoverIndex].label : this.xPoints[index].label,
                    index,
                    yKey: data.key,
                    data,
                    ...this.rects[index]
                }
                this.$emit('mouseover:point', { event, data: this.hoverData })
            }
        },
        handlerMouseOut (event) {
            this.hover = false
            this.hoverIndex = -1
            this.$emit('mouseout:point', { event })
        },
        makeDrawData () {
            if (this.yKeys.length === 0) {
                this.draw = false
                return
            }
            this.draw = true
            this.widthValue = this.$refs.container.getBoundingClientRect().width || 0
            const options = { multiOffset: this.multiOffset, minBarHeight: this.minBarHeight, stacked: this.stacked, barPaddingRate: this.barPaddingRate, yGrid: this.yGrid }
            const points = genRect(this.data, this.yKeys, this.boundary, { max: this.max, min: this.min }, options)
            this.offsetX = (this.boundary.maxX - this.boundary.minX - this.padding) / (this.data.length)
            if (this.stacked) {
                this.stackedRect = points.map((arr, index) => {
                    return arr.reduce((accumulator, currentValue) => {
                        if (currentValue.x) {
                            if (!accumulator.x) {
                                accumulator = {
                                    x: currentValue.x,
                                    y: 0,
                                    width: currentValue.width,
                                    height: 0,
                                    value: 0,
                                    title: '',
                                    key: currentValue.key
                                }
                            }
                            accumulator.height += currentValue.height
                            accumulator.value += currentValue.value
                            accumulator.y = currentValue.y
                            accumulator.title = accumulator.value
                            accumulator.key += '+' + currentValue.key
                        }
                        return accumulator
                    }, {})
                })
            }
            this.rects = []
            points.forEach(point => {
                this.rects = this.rects.concat(point)
            })
        }
    },
    watch: {
        renderOptions: {
            handler (value) {
                this.makeDrawData()
            },
            deep: true
        },
        data: {
            handler (value) {
                if (!value) return
                this.makeDrawData()
            },
            deep: true
        },
        boundary: {
            handler (value) {
                if (!value) return
                this.makeDrawData()
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
.vs-chart-container {
    height: 0;
    position: relative;
    .vs-chart-svg {
        position: absolute;
        top: 0;
        left: 0;
    }

    .bar-shadow {
        -webkit-filter: drop-shadow( 4px 4px 3px rgba(0, 0, 0, .7));
        filter: drop-shadow( 4px 4px 3px rgba(0, 0, 0, .7));
      }
}
</style>
